import { RouterLink as Link } from "../../router";
import { RotateCwIcon, ArrowRight } from "../../icons";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { IntentBadge } from "../status-badge";
import { sectionColor, sectionAccentVars } from "../../tokens";
import { cn } from "../../utils";
import { TextureOverlay, GrainOverlay } from "../grain-overlay";
import { SectionProgressRing } from "./section-progress-ring";
import { getSectionIcon } from "./section-registry-ui";
import {
  type AuditFindingVm,
  type AuditFindingSeverity,
  scoreIntent,
  freshnessLabel,
  useRunAudit,
  FindingRow,
} from "./section-audit-shared";

/**
 * Per-department "best-in-class audit" dashboard card. Speaks the same visual
 * language as the section onboarding card (`section-setup.tsx`): the section's
 * own accent hue (`sectionAccentVars`) driving a `.section-gradient` wash with
 * the dots + grain texture behind the content, and a `SectionProgressRing`
 * medallion holding the section icon — here the ring fills to the latest audit
 * SCORE (100 = best-in-class) so a glance reads "how good is this function."
 *
 * Beneath the identity band: the freshness/score line, a Run/Re-audit button,
 * and the high-priority findings (the open issues, most-severe first, capped so
 * the card stays scannable) with a one-click Fix. Overflow links to the
 * function's full audit page. This is the card the `/audits` hub tiles into a
 * grid — one per department.
 */

export interface SectionAuditCardProps {
  /** Canonical section slug — drives the accent hue + icon. */
  sectionSlug: string;
  sectionLabel: string;
  /** One-line description of what this audit checks. */
  focus: string;
  /** Latest agent score 0-100, or null if never run / last run had none. */
  score: number | null;
  /** Days since last successful audit; null = never run. */
  ageDays: number | null;
  cadenceDays: number;
  /** A re-audit is due (never run or older than cadence). */
  isDue: boolean;
  /** An audit is currently running for this section. */
  running: boolean;
  /** Status of the most recent run (for surfacing errors). */
  lastRunStatus:
    | "running"
    | "completed"
    | "error"
    | "timeout"
    | "superseded"
    | null;
  findings: AuditFindingVm[];
  /** Link to the function's full audit page (overflow + "open" affordance). */
  href: string;
  onRunAudit: () => Promise<void>;
  onFix: (findingId: number) => Promise<void>;
  onDismiss: (findingId: number) => Promise<void>;
}

const SEVERITY_RANK: Record<AuditFindingSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** How many findings show inline before the "+N more" overflow link. */
const SHOWN = 3;

export function SectionAuditCard({
  sectionSlug,
  sectionLabel,
  focus,
  score,
  ageDays,
  cadenceDays,
  isDue,
  running,
  lastRunStatus,
  findings,
  href,
  onRunAudit,
  onFix,
  onDismiss,
}: SectionAuditCardProps) {
  const { busy, error, runAudit } = useRunAudit(onRunAudit, running);
  const accent = sectionColor(sectionSlug);
  const SectionIcon = getSectionIcon(sectionSlug);

  const open = findings
    .filter((f) => f.status === "open" || f.status === "fixing")
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  const shown = open.slice(0, SHOWN);
  const hidden = open.length - shown.length;
  const highCount = open.filter((f) => f.severity === "high").length;

  return (
    <Card
      data-section-audit-card={sectionSlug}
      style={sectionAccentVars(sectionSlug)}
      className="section-shine-border relative isolate flex flex-col overflow-hidden py-0"
    >
      {/* Section-tinted gradient wash + accent halftone + film grain, all behind
          the z-10 content and clipped to the card's rounded corners. */}
      <div
        aria-hidden
        className="section-gradient pointer-events-none absolute inset-0 z-0"
      />
      <TextureOverlay variant="dots" />
      <GrainOverlay />

      <CardContent className="relative z-10 flex flex-1 flex-col gap-0 p-0">
        {/* Identity band — medallion (score arc + icon), name + freshness, run. */}
        <div className="flex items-start gap-4 px-6 pb-4 pt-5">
          <SectionProgressRing value={score ?? 0} accent={accent} size={64} stroke={6}>
            <SectionIcon className="size-6" style={{ color: accent }} aria-hidden />
          </SectionProgressRing>

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate type-title font-medium text-foreground">
                {sectionLabel}
              </h3>
              {score != null ? (
                <IntentBadge intent={scoreIntent(score)} className="tabular-nums">
                  {score}/100
                </IntentBadge>
              ) : null}
            </div>
            <p className="truncate type-body text-muted-foreground">{focus}</p>
            <p className="section-fg mt-0.5 type-body tabular-nums">
              {freshnessLabel(ageDays, cadenceDays, isDue, true)}
            </p>
          </div>

          <Button
            type="button"
            variant={isDue ? "section" : "section-outline"}
            size="sm"
            onClick={runAudit}
            disabled={busy}
            aria-label={`Run the ${sectionLabel} best-in-class audit`}
          >
            <RotateCwIcon aria-hidden="true" className={cn(busy && "animate-spin")} />
            {busy ? "Auditing…" : ageDays == null ? "Run audit" : "Re-audit"}
          </Button>
        </div>

        {error ? (
          <p className="px-6 pb-3 type-body text-destructive">{error}</p>
        ) : null}

        {lastRunStatus === "error" || lastRunStatus === "timeout" ? (
          <p className="px-6 pb-3 type-body text-warning-fg">
            The last audit run {lastRunStatus === "timeout" ? "timed out" : "failed"}.
            Try running it again.
          </p>
        ) : null}

        {/* High-priority findings — the actionable half of the card. */}
        <div className="mt-auto border-t border-border/40 bg-card/40 px-6 py-4">
          {open.length === 0 ? (
            <p className="type-body text-muted-foreground">
              {ageDays == null
                ? `Run the first audit to check whether ${sectionLabel} is best-in-class.`
                : `No open findings — ${sectionLabel} looks best-in-class.`}
            </p>
          ) : (
            <>
              <p className="mb-2 type-subtext text-muted-foreground/70">
                {open.length} open finding{open.length === 1 ? "" : "s"}
                {highCount > 0 ? ` · ${highCount} high-priority` : ""}
              </p>
              <ul aria-label={`${sectionLabel} audit findings`} className="flex flex-col">
                {shown.map((f, idx) => (
                  <li
                    key={f.id}
                    className={cn(
                      "border-b border-border/40 py-2.5",
                      idx === 0 && "pt-0",
                      idx === shown.length - 1 && hidden === 0 && "border-b-0 pb-0",
                    )}
                  >
                    <FindingRow
                      finding={f}
                      onFix={onFix}
                      onDismiss={onDismiss}
                      fixVariant="section"
                      showDetail={false}
                      gapClassName="gap-1.5"
                      actionsGapClassName="gap-1"
                    />
                  </li>
                ))}
              </ul>
              {hidden > 0 ? (
                <Link
                  href={href}
                  className="section-fg mt-2 inline-flex items-center gap-1 type-body hover:underline"
                >
                  {hidden} more finding{hidden === 1 ? "" : "s"}
                  <ArrowRight aria-hidden="true" className="size-3.5" />
                </Link>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
